import React, { useEffect, useRef, useState, useCallback } from 'react';
import useUploadContext from 'hooks/useUploadContext';
import VolumeButton from 'components/shared/VolumeButton'
import "./index.scss";

const Player = () => {
    const {
        layers,
        play,
        setPlay,
        time,
        setTime,
        mutes,
        layersUpdated,
        setLayersUpdated,
        volume,
        setVolume,
        speed,
        audioContextRef,
        manualCurrentTimeMusic,
        setManualCurrentTimeMusic
    } = useUploadContext();
    const nodesRef = useRef([]);
    const [buffers, setBuffers] = useState([]);
    // const scriptProcessorRef = useRef(null);
    const intervalRef = useRef(null);

    const resetNodes = () => {
        nodesRef.current.forEach(({ source, gainNode }) => {
            source?.stop();
            gainNode?.disconnect();
        });
        nodesRef.current = [];
    };

    const createBuffers = useCallback(async (data) => {
        const audioContext = audioContextRef.current;
        const newBuffers = [];
        for (const layer of data) {
            if (layer?.file) {
                const audioData = await layer.file.blob.arrayBuffer();
                const audioBuffer = await audioContext.decodeAudioData(audioData);
                newBuffers.push(audioBuffer);
            } else {
                newBuffers.push(null);
            }
        }
        setBuffers(newBuffers);
    }, [audioContextRef]);

    const createAndStartNode = useCallback((audioBuffer, layerMute, layersTime, volumeValue, playbackRate) => {
        const audioContext = audioContextRef.current;
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;

        const gainNode = audioContext.createGain();
        gainNode.gain.value = layerMute ? 0 : volumeValue;
        source.playbackRate.value = playbackRate;
        source.connect(gainNode).connect(audioContext.destination);

        source.start(0, layersTime);

        nodesRef.current.push({ source, gainNode });
    }, [audioContextRef]);

    const handleStop = () => {
        resetNodes();
        if (audioContextRef.current && audioContextRef.current.state === "running") {
            audioContextRef.current.suspend();
        }
    }
    const handlePlay = () => {
        if (!audioContextRef.current) return;
        audioContextRef.current.resume().then(() => {
            resetNodes();
            buffers.forEach((buffer, index) => {
                buffer && createAndStartNode(buffer, mutes[index], time, volume / 100, speed);
            });
        });
    };

    const handleClick = () => {
        if (!audioContextRef.current) return;
        if (play) {
            handleStop();
        } else {
            handlePlay();
        }
        setPlay(!play);
    };

    useEffect(() => {
        if (layersUpdated) {
            resetNodes();
            createBuffers(layers);
            if (play) {
                handlePlay();
            }
            setLayersUpdated(false);
        }
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [layers, layersUpdated]);

    useEffect(() => {
        if (play) {
            handlePlay();
        } else {
            handleStop();
        }
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [buffers, play]);

    useEffect(() => {
        if (play) handlePlay();
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [mutes, speed]);


    useEffect(() => {
        nodesRef.current.forEach(({ gainNode }) => {
            if (gainNode) {
                gainNode.gain.value = volume / 100;
            }
        });
    }, [volume]);

    const handleVolume = (i) => {
        setVolume((pre) => {
            const newValue = pre + i;
            if (newValue > 100) {
                return 100;
            } else if (newValue < 0) {
                return 0;
            } else {
                return newValue;
            }
        });
    };

    useEffect(() => {
        if (play) {
            audioContextRef.current.baseTime = audioContextRef.current.currentTime - time;
            intervalRef.current = setInterval(() => {
                if (audioContextRef.current) {
                    setTime(audioContextRef.current.currentTime - audioContextRef.current.baseTime);
                }
            }, 1);
        } else {
            clearInterval(intervalRef.current)
        }
        return () => clearInterval(intervalRef.current);
    }, [intervalRef, audioContextRef, play, manualCurrentTimeMusic]);

    useEffect(() => {
        if(manualCurrentTimeMusic) {
            handleStop();
            setTime(manualCurrentTimeMusic);
            setManualCurrentTimeMusic(null);
            handlePlay();
        }
    }, [manualCurrentTimeMusic]);

    return (
        <div className="play-button-wrapper">
            <VolumeButton
                handleClick={handleClick}
                active={play}
                step={3}
                setValue={handleVolume}
                value={volume}
                hasRotation={volume >= 0 || volume <= 100}
            />
        </div>
    );
};

export default Player;
